/** French-first user messages for HTTP status; detail from JSON body when present. */

/** Thrown by admin `api` layer so callers can parse validation payloads. */
export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly bodyText: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

const STATUS_FR: Record<number, string> = {
  400: 'Requête invalide.',
  401: 'Session expirée ou non autorisée. Reconnectez-vous.',
  403: 'Accès refusé.',
  404: 'Élément introuvable.',
  409: 'Conflit : les données ont peut-être changé.',
  413: 'Fichier trop volumineux.',
  422: 'Données invalides.',
  429: 'Trop de requêtes. Réessayez dans un instant.',
  500: 'Erreur serveur. Réessayez plus tard.',
  502: 'Service indisponible.',
  503: 'Service temporairement indisponible.',
};

function extractMessageFromBody(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('<')) return '';
  try {
    const j = JSON.parse(trimmed) as { message?: unknown; error?: string };
    if (typeof j.message === 'string') return j.message;
    if (Array.isArray(j.message)) {
      const parts: string[] = [];
      for (const item of j.message) {
        if (typeof item === 'string') {
          parts.push(item);
          continue;
        }
        if (item && typeof item === 'object' && 'constraints' in item) {
          const row = item as { constraints?: Record<string, string> };
          const first =
            row.constraints && typeof row.constraints === 'object' ? Object.values(row.constraints)[0] : undefined;
          if (first) parts.push(first);
        }
      }
      return parts.join(' ');
    }
    if (typeof j.error === 'string') return j.error;
  } catch {
    /* plain text */
  }
  return trimmed.length > 280 ? `${trimmed.slice(0, 280)}…` : trimmed;
}

const LOGIN_CREDENTIAL_MESSAGES = new Set([
  'invalid email or password',
  'email ou mot de passe invalide',
  'e-mail ou mot de passe incorrect.',
  'email ou mot de passe incorrect.',
  'e-mail ou mot de passe incorrect',
  'email ou mot de passe incorrect',
]);

function isLoginCredentialDetail(detail: string): boolean {
  const normalized = detail.trim().toLowerCase();
  if (LOGIN_CREDENTIAL_MESSAGES.has(normalized)) return true;
  return /mot de passe incorrect|invalid email or password/i.test(normalized);
}

/** Build a single French-friendly line for failed responses. */
export function messageFromFailedResponse(status: number, bodyText: string): string {
  const base = STATUS_FR[status] ?? `Erreur ${status}`;
  const detail = extractMessageFromBody(bodyText);
  if (!detail || detail === base) return base;
  if (status === 403 && detail !== base) return detail;
  if (status === 401 && isLoginCredentialDetail(detail)) {
    return 'E-mail ou mot de passe incorrect.';
  }
  if (/^[A-Za-z\s]+$/.test(detail) && !/[àâéèêëïîôùûüÿçœæ]/.test(detail)) return base;
  if (detail.length > 120 || /prisma|invocation|\.ts:|\.js:|stack trace/i.test(detail)) return base;
  return `${base} (${detail})`;
}

function isAbortLikeError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof Error && /aborted/i.test(error.message)) return true;
  return false;
}

function isNetworkLikeError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.trim().toLowerCase();
  if (msg === 'failed to fetch' || msg === 'networkerror when attempting to fetch resource') return true;
  if (error.name === 'TypeError' && /fetch|network/i.test(error.message)) return true;
  return false;
}

/** Normalize any thrown value to a display string (after api throws Error). */
export function getDisplayError(error: unknown): string {
  if (isAbortLikeError(error)) {
    return 'La requête a été interrompue. Réessayez dans un instant.';
  }
  if (isNetworkLikeError(error)) {
    return 'Impossible de joindre le serveur. Vérifiez la connexion et réessayez.';
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * Map Nest ValidationPipe / class-validator payloads to field keys (snake_case from API → same keys).
 * Returns `null` if body is not a structured validation error.
 */
export function parseFieldErrorsFromHttpError(e: unknown): Record<string, string> | null {
  if (!(e instanceof HttpError)) return null;
  if (e.status !== 400 && e.status !== 422) return null;
  const trimmed = e.bodyText.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const j = JSON.parse(trimmed) as { message?: unknown };
    if (j.message == null) return null;
    const out: Record<string, string> = {};
    if (Array.isArray(j.message)) {
      for (const item of j.message) {
        if (typeof item === 'string') {
          out._form = (out._form ? `${out._form} ` : '') + item;
          continue;
        }
        if (item && typeof item === 'object' && 'property' in item) {
          const row = item as { property?: string; constraints?: Record<string, string> };
          const prop = row.property ?? '';
          const first =
            row.constraints && typeof row.constraints === 'object'
              ? Object.values(row.constraints)[0]
              : undefined;
          if (prop && first) out[prop] = first;
        }
      }
    } else if (typeof j.message === 'string') {
      out._form = j.message;
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}
